import { Model, Factory, belongsTo, trait, hasMany, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randFirstName,
  randLastName,
  randEmail,
  randUuid,
  randPhrase,
  randChanceBoolean,
  rand,
} from '@ngneat/falso'
import { USER_WORK_ROLES, type UserAttributes } from '@/models/users.js'
import {
  sortable,
  pageable,
  createErrorResponse,
  filterable,
} from '@@/api/mock-server/utils.js'
import type { MirageServer, MockServer } from '@@/api/mock-server/server.js'

export const model = (Model as ModelDefinition<UserAttributes>).extend({
  permissionGroup: belongsTo(),
  availableDepartments: hasMany('department'),
  activeDepartment: belongsTo('department'),
  availableDepartmentGroups: hasMany('departmentGroup'),
  homeDepartment: belongsTo('department'),
  earliestAssignment: belongsTo('locumAssignment'),
})

export const factory = Factory.extend<UserAttributes>({
  first_name() {
    return randFirstName()
  },
  last_name() {
    return randLastName()
  },
  email() {
    return randEmail({
      firstName: this.first_name as string,
      lastName: this.last_name as string,
      provider: 'example.org',
    })
  },
  initials() {
    return (
      (this.first_name as string).charAt(0) +
      (this.last_name as string).charAt(0)
    )
  },
  avatar_initials() {
    return (
      (this.first_name as string).charAt(0) +
      (this.last_name as string).charAt(0)
    )
  },
  all_departments_active() {
    return false
  },
  language() {
    return 'en'
  },
  settings() {
    return {
      provet_theme: 'DEFAULT',
    }
  },
  is_saml_enabled() {
    return true
  },
  key() {
    return randUuid()
  },
  virtual_user() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  qualifications() {
    return randPhrase({ maxCharCount: 60 })
  },
  is_locum() {
    return false
  },
  is_active() {
    return true
  },
  work_role() {
    return rand([null, ...Object.values(USER_WORK_ROLES)])
  },
  withIsLocum: trait({
    afterCreate(user) {
      user.update({ is_locum: true })
    },
  }),
  withInactive: trait({
    afterCreate(user) {
      user.update({ is_active: false })
    },
  }),
  afterCreate(user, server) {
    if (!user.permissionGroup) {
      const groups = server.schema.all('permissionGroup')

      // if there are permission groups, pick one, else create one
      const permissionGroup = groups.length
        ? groups.models[(user.id - 1) % groups.models.length]
        : server.schema.create('permissionGroup')

      user.update({ permissionGroup })
    }

    if (user.availableDepartments.length === 0) {
      const availableDepartments = server.schema.all('department')
      user.update({ availableDepartments })
    }

    if (user.activeDepartment === null) {
      const activeDepartment = server.schema.first('department')
      user.update({ activeDepartment })
    }

    if (user.availableDepartmentGroups.length === 0) {
      const availableDepartmentGroups = server.schema.all('departmentGroup')
      user.update({ availableDepartmentGroups })
    }
  },
  withActiveDepartment: trait({
    afterCreate(user, server) {
      user.update({
        activeDepartment: server.schema.first('department'),
      })
    },
  }),
  withHomeDepartment: trait({
    afterCreate(user, server) {
      const allDepartments = server.schema.all('department').models
      user.update({
        homeDepartment: allDepartments[allDepartments.length - 1],
      })
    },
  }),
})

const unknownDepartmentResponse = new Response(
  400,
  {},
  {
    errors: [
      {
        status: '400',
        source: { pointer: '/data/relationships/active_department' },
      },
    ],
  },
)

const unauthenticatedResponse = new Response(403, {
  'X-Provet-Cloud-Not-Authenticated': '1',
})

export function routes(server: MockServer) {
  server.resource('user')

  server.get('/users', function (this: MirageServer, schema, request) {
    const users = schema.all('user')

    // first we sort
    const sorted = sortable(users, request)

    // Apply Search Filter
    const filtered = filterable(sorted, request, {
      search: (item, value) => {
        const firstAndLastName =
          `${item.attrs.first_name} ${item.attrs.last_name}`?.toLowerCase()
        return firstAndLastName.includes(value?.toLowerCase() || '')
      },
      is_active: () => true,
    })

    // then we paginate
    const { data, meta } = pageable(filtered, request)

    // and finally construct the response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get('/users/staff', function (this: MirageServer, schema, request) {
    const users = schema.all('user')

    // Sort
    const sorted = sortable(users, request)

    // Apply filtering
    const filtered = filterable(sorted, request, {
      search: (item, value) => {
        const firstAndLastName =
          `${item.attrs.first_name} ${item.attrs.last_name}`?.toLowerCase()
        return firstAndLastName.includes(value?.toLowerCase() || '')
      },
      role: (item, value) => {
        if (value === 'vet') {
          return item.attrs.work_role === USER_WORK_ROLES.Veterinarian
        }
        if (value === 'nurse') {
          // nurse filter returns users with work_role VET or SUPPORT
          return (
            item.attrs.work_role === USER_WORK_ROLES.Veterinarian ||
            item.attrs.work_role === USER_WORK_ROLES.Support
          )
        }
        return true
      },
      'department.id': (item, value) =>
        item.availableDepartments.models.some(
          (department: { id: string }) => department.id === value,
        ),
    })

    // Paginate
    const { data, meta } = pageable(filtered, request)

    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get('/users/assignable', function (_schema, request) {
    const users = _schema.all('user')

    const sorted = sortable(users, request)

    const filtered = filterable(sorted, request, {
      search: (item, value) => {
        const fullName =
          `${item.attrs.first_name} ${item.attrs.last_name}`.toLowerCase()
        return fullName.includes(value?.toLowerCase() || '')
      },
      is_active: () => true,
    })

    const { data, meta } = pageable(filtered, request)

    // Return AssignableUser shape (subset of User attributes)
    return {
      data: data.models.map((user) => ({
        type: 'users' as const,
        id: user.id,
        attributes: {
          first_name: user.attrs.first_name,
          last_name: user.attrs.last_name,
          initials: user.attrs.initials,
          avatar_initials: user.attrs.avatar_initials,
          avatar_image_url: '',
          work_role: user.attrs.work_role,
          is_active: user.attrs.is_active ?? true,
        },
      })),
      meta: { pagination: meta },
    }
  })

  server.get('/users/current', function (schema) {
    const user = schema.first('user')

    // emulate un-authenticated state
    if (!user) {
      return unauthenticatedResponse
    }

    return user
  })

  server.get('/users/:id', function (schema, request) {
    const id = request.params.id
    const user = schema.find('user', id)

    if (!user) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `User with id ${id} not found` }],
      })
    }

    return user
  })

  server.post('/users', function (schema, request) {
    const attrs = JSON.parse(request.requestBody)
    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    const user = schema.create('user', attrs.data.attributes)
    return user
  })

  server.patch('/users/:id', async function (schema, request) {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const user = schema.find('user', id)

    if (!user) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `User with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    await user.update(attrs.data.attributes)
    return user
  })

  server.patch('/users/current', function (schema, request) {
    const user = schema.first('user')

    // emulate un-authenticated state
    if (!user) {
      return unauthenticatedResponse
    }

    const body = JSON.parse(request.requestBody)
    const department = schema.find(
      'department',
      body.data.relationships.active_department.data.id,
    )

    // mimic real API error response
    if (!department) {
      return unknownDepartmentResponse
    }

    user.update({ activeDepartment: department })

    // Also update the merged settings to reflect the new active department
    const mergedSettings = schema.first('mergedSettings')
    if (mergedSettings) {
      // Central department is Bath Clinic (ID '1')
      const CENTRAL_DEPARTMENT_ID = '1'
      const isCentral = department.id === CENTRAL_DEPARTMENT_ID
      mergedSettings.update({
        activeDepartment: department,
        is_central_department: isCentral,
      })
    }

    return user
  })
}

export function seeds(server: MockServer) {
  server.createList('user', 42)
}
